// Pure helpers for the agent settings form. The form keeps raw text for
// inputs (comma lists, numbers) and converts only when saving.

export interface AgentForm {
  name: string;
  enabled: boolean;
  companyName: string;
  companyDetails: string;
  offerDetails: string;
  goal: string;
  faqs: string;
  policies: string;
  tone: string;
  instructions: string;
  guardrails: string;
  forbiddenTopicsText: string;
  maxWords: string;
  maxParagraphs: string;
  signature: string;
  signatureIncluded: boolean;
  resourceLinks: any[];
  sendMode: "review" | "auto";
  videoMode: "off" | "manual" | "auto";
}

const text = (value: any) => (value === undefined || value === null ? "" : String(value));

export const toForm = (agent: any): AgentForm => ({
  name: text(agent.name),
  enabled: Boolean(agent.enabled),
  companyName: text(agent.companyName),
  companyDetails: text(agent.companyDetails),
  offerDetails: text(agent.offerDetails),
  goal: text(agent.goal),
  faqs: text(agent.faqs),
  policies: text(agent.policies),
  tone: agent.tone || "friendly",
  instructions: text(agent.instructions),
  guardrails: text(agent.guardrails),
  forbiddenTopicsText: (agent.forbiddenTopics || []).join(", "),
  maxWords: text(agent.maxWords),
  maxParagraphs: text(agent.maxParagraphs),
  signature: text(agent.signature),
  signatureIncluded: Boolean(agent.signatureIncluded),
  resourceLinks: agent.resourceLinks || [],
  sendMode: agent.sendMode === "auto" ? "auto" : "review",
  videoMode: agent.videoMode === "off" || agent.videoMode === "auto" ? agent.videoMode : "manual",
});

export const splitList = (value: string) =>
  value
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);

// Builds the PUT /ai/agent payload. Examples are managed separately.
// Blank number fields are left out so the server keeps its current value.
export const toPayload = (form: AgentForm) => {
  const { forbiddenTopicsText, maxWords, maxParagraphs, ...rest } = form;
  const payload: any = { ...rest, forbiddenTopics: splitList(forbiddenTopicsText) };
  if (maxWords.trim() !== "") payload.maxWords = Number(maxWords);
  if (maxParagraphs.trim() !== "") payload.maxParagraphs = Number(maxParagraphs);
  return payload;
};

const MISSING_LABEL: Record<string, string> = {
  companyName: "Company name",
  companyDetails: "Company details",
};

export const missingLabel = (key: string) => MISSING_LABEL[key] || key;
